import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, FormControlLabel, Switch, Alert, Stack, MenuItem } from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { productService } from '../../services/product.service';
import postingService from '../../services/posting.service';
import { Product, Category } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import DimensionSelector, { DimensionSelectorRef } from '../../components/DimensionSelector';

const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [postingProfiles, setPostingProfiles] = useState<any[]>([]);
  const dimRef = useRef<DimensionSelectorRef>(null);
  const [product, setProduct] = useState<Partial<Product>>({
    sku: '', name: '', description: '', categoryId: 0, unitPrice: 0, costPrice: 0, reorderLevel: 0, unit: 'PCS', isActive: true, productPostingProfileId: null,
  });

  useEffect(() => { loadCategories(); loadPostingProfiles(); if (id) loadProduct(); }, [id]);

  const loadCategories = async () => { try { setCategories(await productService.getCategories()); } catch { } };
  const loadPostingProfiles = async () => { try { setPostingProfiles((await postingService.getProductProfiles()).data); } catch { } };
  const loadProduct = async () => { try { setProduct(await productService.getById(Number(id))); } catch { setError('Failed to load product'); } };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      let entityId = id ? Number(id) : 0;
      if (id) await productService.update(Number(id), product);
      else { const created = await productService.create(product); entityId = created.id || 0; }

      try {
        await dimRef.current?.save('PRODUCT', entityId);
      } catch (dimErr: any) {
        setError(dimErr.response?.data?.details || dimErr.response?.data?.message || 'Error saving dimensions');
        setLoading(false); return;
      }

      navigate('/products');
    } catch (err: any) { setError(err.response?.data?.details || err.response?.data?.message || 'Failed to save product'); }
    finally { setLoading(false); }
  };

  const handleChange = (field: keyof Product) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setProduct({ ...product, [field]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{id ? t('products.edit') : t('products.new')}</Typography>
      <Paper sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label={t('products.sku')} value={product.sku} onChange={handleChange('sku')} disabled={loading} required />
              <TextField fullWidth label={t('products.product_name')} value={product.name} onChange={handleChange('name')} disabled={loading} required />
            </Box>
            <TextField fullWidth label={t('common.description')} value={product.description} onChange={handleChange('description')} disabled={loading} multiline rows={3} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth select label={t('products.category')} value={product.categoryId || ''}
                onChange={e => setProduct({ ...product, categoryId: Number(e.target.value) })} disabled={loading}>
                {categories.map(cat => <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>)}
              </TextField>
              <TextField fullWidth select label={t('products.unit')} value={product.unit}
                onChange={e => setProduct({ ...product, unit: e.target.value })} disabled={loading}>
                <MenuItem value="PCS">Pieces</MenuItem><MenuItem value="KG">Kilograms</MenuItem>
                <MenuItem value="L">Liters</MenuItem><MenuItem value="M">Meters</MenuItem><MenuItem value="BOX">Box</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth type="number" label={t('products.unit_price')} value={product.unitPrice} onChange={handleChange('unitPrice')} disabled={loading} inputProps={{ step: '0.01', min: '0' }} />
              <TextField fullWidth type="number" label={t('products.cost_price')} value={product.costPrice} onChange={handleChange('costPrice')} disabled={loading} inputProps={{ step: '0.01', min: '0' }} />
              <TextField fullWidth type="number" label={t('products.reorder_level')} value={product.reorderLevel} onChange={handleChange('reorderLevel')} disabled={loading} inputProps={{ min: '0' }} />
            </Box>
            <TextField fullWidth select label={t('posting.product_posting_profile')} value={product.productPostingProfileId || ''}
              onChange={e => setProduct({ ...product, productPostingProfileId: e.target.value ? Number(e.target.value) : null })} disabled={loading}>
              <MenuItem value="">{t('common.none')}</MenuItem>
              {postingProfiles.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.profileName}</MenuItem>)}
            </TextField>
            <FormControlLabel control={<Switch checked={product.isActive} onChange={e => setProduct({ ...product, isActive: e.target.checked })} disabled={loading} />} label={t('common.active')} />
            <DimensionSelector ref={dimRef} entityType="PRODUCT" entityId={id ? Number(id) : undefined} disabled={loading} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>{loading ? t('common.saving') : t('common.save')}</Button>
              <Button variant="outlined" startIcon={<Cancel />} onClick={() => navigate('/products')} disabled={loading}>{t('common.cancel')}</Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductForm;
